import React, { useEffect, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import styled from 'styled-components';

import { getActionCenterSummary } from 'apis/ActionCenterAPI';
import { ACTION_CENTER_QUERY_KEY, ACTION_CENTER_ROUTE } from 'constants/actionCenter';
import { canViewActionCenter } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import ActionCenterItems from './ActionCenterItems';

const ACTION_CENTER_REFRESH_INTERVAL_MS = 15_000;

const FloatingContainer = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;

  @media (max-width: 768px) {
    right: 16px;
    bottom: 16px;
  }
`;

const Panel = styled.section`
  width: 440px;
  max-width: calc(100vw - 32px);
  max-height: min(560px, calc(100vh - 112px));
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--line-gray);
  border-radius: 12px;
  background: var(--white);
  box-shadow: 0 8px 24px var(--box-shadow-gray-dark);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--line-gray);
`;

const CloseButton = styled(Button)`
  flex: 0 0 auto;
`;

const PanelTitle = styled(Link)`
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 16px;
  text-decoration: none;

  &:hover {
    color: var(--button-blue);
    text-decoration: none;
  }
`;

const PanelBody = styled.div`
  min-height: 96px;
  overflow-y: auto;
`;

const FloatingButton = styled(Button)`
  height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-radius: 22px;
  box-shadow: 0 4px 14px var(--box-shadow-gray-dark);
  font-family: var(--font-bold);
`;

const CountBadge = styled.span`
  min-width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 11px;
  background: var(--white);
  color: var(--button-blue);
  font-family: var(--font-bold);
  font-size: 12px;
`;

const ActionCenter = () => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [isOpen, setIsOpen] = useState(false);
  const actionCenterEnabled = canViewActionCenter(currentUser);

  const { data: summary } = useQuery({
    queryKey: [ACTION_CENTER_QUERY_KEY.ROOT, ACTION_CENTER_QUERY_KEY.SUMMARY, currentUser?.user_id],
    queryFn: getActionCenterSummary,
    enabled: actionCenterEnabled,
    refetchInterval: ACTION_CENTER_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    setIsOpen(false);
  }, [currentUser?.user_id]);

  if (!actionCenterEnabled || !summary?.total_count) {
    return null;
  }

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <FloatingContainer>
        {isOpen && (
          <Panel aria-label="Action Center">
            <PanelHeader>
              <PanelTitle to={ACTION_CENTER_ROUTE.PATH} onClick={() => setIsOpen(false)}>
                Action Center
              </PanelTitle>
              <CloseButton
                aria-label="Close Action Center"
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => setIsOpen(false)}
              />
            </PanelHeader>
            <PanelBody>
              <ActionCenterItems enabled={isOpen} />
            </PanelBody>
          </Panel>
        )}
        <FloatingButton
          aria-expanded={isOpen}
          aria-label={`Action Center, ${summary.total_count} pending actions`}
          type="primary"
          onClick={() => setIsOpen((open) => !open)}
        >
          Action Center
          <CountBadge>{summary.total_count}</CountBadge>
        </FloatingButton>
      </FloatingContainer>
    </ClickAwayListener>
  );
};

export default ActionCenter;
